import { generateHTML } from "@tiptap/html";
import moment from "moment";
import { useEffect, useRef } from "react";
import { AvatarImage } from "../../Components/AvatarImage/AvatarImage";
import IconButton from "../../Components/IconButton/IconButton";
import { replaceAll } from "../../Helper/String";
import { sanitizeRichHtml } from "../../Helper/sanitize";
import { useNewsStore } from "../../Stores/News";
import { useRouterStore } from "../../Stores/Router";
import { useConfigStore } from "../../Stores/Config";
import { closeWidgetAndReset } from "../../Components/WidgetHeader/WidgetHeader";
import {
  resolveLocalizedContent,
  resolveLocalizedString,
} from "../../Helper/LocalizedValue";
import { ReactComponent as ChevronLeft } from "../../Assets/angle-left-solid.svg";
import { ReactComponent as XIcon } from "../../Assets/x-mark.svg";
import ExtensionKit from "../../Components/tiptap/extenstions/extension-kit";
import { ChangelogComments } from "../../Components/ChangelogComments/ChangelogComments";
import "./NewsDetails.scss";

function NewsDetails() {
  const newsStore = useNewsStore();
  const routerStore = useRouterStore();
  const configStore = useConfigStore();
  const currentArticle = newsStore.currentArticle;
  const headerTitleRef = useRef(null as any);
  const headerImageRef = useRef(null as any);
  const showBackButton = routerStore.showBackButton;
  const showShadow = true;
  const showTitle = true;

  useEffect(() => {
    // currentData is the router's global page payload. When a gleap://article
    // link inside the news body pushes "helpcenter-article", this still-mounted
    // view would otherwise re-fire with the HELP CENTER article id and load it
    // as a news article — the resulting "no article" rescue then yanked the
    // widget to the home screen (#145642).
    if (routerStore.currentPage !== "newsdetails") {
      return;
    }
    if (routerStore.currentData && routerStore.currentData.id) {
      newsStore.loadArticle(routerStore.currentData.id);
    } else {
      // Nothing to load: leave instead of sitting on the skeleton forever (#147330).
      routerStore.setPage("menu");
    }
  }, [routerStore.currentData]);

  // Subject/message may arrive as unresolved localized objects from older
  // API-created news — resolve to plain values so React never receives an
  // object child (#143258).
  const currentLanguage = configStore.getCurrentLanguage();
  const subject = resolveLocalizedString(currentArticle?.subject, currentLanguage);

  const renderContent = () => {
    var content = "";
    try {
      if (currentArticle) {
        const messageContent = resolveLocalizedContent(
          currentArticle.message,
          currentLanguage,
        );
        if (messageContent) {
          content = generateHTML(messageContent, [...ExtensionKit()]);
          content = sanitizeRichHtml(replaceAll(content, "\n", "<br>"));
        }
      }
    } catch (error) {
      console.log(error);
    }

    return (
      <>
        <div
          className={`news-header ${showShadow && "news-header--shadow"} ${
            showTitle && "news-header--title"
          }`}
        >
          <span>
            {currentArticle?.config?.pinned && "📌 "} {subject}
          </span>
        </div>
        <div className="news-header--placeholder" />
        {currentArticle?.coverImageUrl ? (
          <img
            ref={headerImageRef}
            className="news-content-image"
            src={currentArticle?.coverImageUrl}
            alt=""
          />
        ) : (
          <div ref={headerImageRef} className="news-content-image" />
        )}
        {currentArticle ? (
          <>
            <div className="news-title" ref={headerTitleRef} role="heading" aria-level={1}>
              {currentArticle?.config?.pinned && "📌 "}{" "}
              {subject}
            </div>
            <div className="news-content">
              {currentArticle.sender && (
                <div className="news-content-meta">
                  <AvatarImage
                    src={currentArticle.sender.profileImageUrl}
                    alt={currentArticle.sender.firstName}
                  />
                  <span>
                    {currentArticle.sender.firstName} •{" "}
                    {currentArticle?.sentAt
                      ? moment(currentArticle?.createdAt).fromNow()
                      : moment(currentArticle?.sentAt).fromNow()}
                  </span>
                </div>
              )}
              <div
                className="news-content-body"
                dangerouslySetInnerHTML={{ __html: content }}
              />
              {currentArticle.subType === "CHANGELOG" && currentArticle.id && (
                <ChangelogComments changelogId={currentArticle.id} />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="news-title">
              <div
                className="skeleton-box"
                style={{
                  minWidth: "80%",
                  minHeight: "18px",
                }}
              />
            </div>
            <div className="news-content-skeleton">
              <div
                className="skeleton-box"
                style={{
                  minWidth: "68%",
                  minHeight: "16px",
                  marginBottom: "7px",
                }}
              />
              <div
                className="skeleton-box"
                style={{
                  minWidth: "70%",
                  minHeight: "16px",
                  marginBottom: "7px",
                }}
              />
              <div
                className="skeleton-box"
                style={{
                  minWidth: "65%",
                  minHeight: "16px",
                  marginBottom: "7px",
                }}
              />
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="news-details">
      {showBackButton && (
        <div className="back-button">
          <IconButton
            icon={ChevronLeft}
            ariaLabel={configStore.translateTextWithFallback("back", "Back")}
            onClick={() => {
              routerStore.popPage();
            }}
          />
        </div>
      )}
      <IconButton
        className="close-button"
        icon={XIcon}
        ariaLabel={configStore.translateTextWithFallback("close", "Close")}
        onClick={() => {
          closeWidgetAndReset();
        }}
      />
      {renderContent()}
    </div>
  );
}

export default NewsDetails;
